import React, { useEffect, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

const money = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);
const TYPE_LABELS = {
  nightly_retention: 'Nightly retention',
  refund_adjustment: 'Refund adjustment',
  opening_balance: 'Opening balance',
  monthly_settlement_out: 'Month-end settlement (out)',
};

/**
 * Tax Holdings — the merchant's OWN ledger book (read-only, rules-scoped by
 * the callable: merchants read only their own). Transparency is the product:
 * every cent we're holding shows here, entry by entry.
 */
const TaxHoldingsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [enrolled, setEnrolled] = useState(null); // null = still loading
  const [enrollBusy, setEnrollBusy] = useState(false);

  const loadEnrollment = async () => {
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;
    const snap = await getDoc(doc(getFirestore(), 'users', uid));
    setEnrolled(snap.data()?.taxServiceEnabled === true);
  };

  const toggleEnrollment = async () => {
    const uid = getAuth().currentUser?.uid;
    if (!uid || enrollBusy) return;
    setEnrollBusy(true);
    try {
      const next = !(enrolled === true);
      await setDoc(doc(getFirestore(), 'users', uid), {
        taxServiceEnabled: next,
        taxServiceEnabledAt: next ? serverTimestamp() : null,
      }, { merge: true });
      setEnrolled(next);
    } finally {
      setEnrollBusy(false);
    }
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await httpsCallable(getFunctions(), 'getTaxLedger')({});
      setData(res.data);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); loadEnrollment(); }, []);

  return (
    <div className="card mb-4" style={{ border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden' }}>
      <div style={{ background: '#1f2937', color: '#fff', padding: '18px 24px' }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: '#f97316', fontWeight: 800 }}>Tax Holdings</div>
            <h4 className="mb-0 font-weight-bold" style={{ color: '#fff' }}>Your tax book</h4>
            <div style={{ fontSize: 13, color: '#9ca3af' }}>Every cent set aside for your taxes — entry by entry.</div>
          </div>
          <button className="btn btn-sm btn-outline-light" onClick={load} disabled={loading}>
            <i className="fas fa-rotate"></i>
          </button>
        </div>
      </div>

      {enrolled !== null && (
        <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ padding: '14px 24px', background: enrolled ? '#f0fdf4' : '#fffbeb', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ maxWidth: 640 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: enrolled ? '#166534' : '#92400e' }}>
              <i className={`fas ${enrolled ? 'fa-check-circle' : 'fa-envelope'} mr-1`}></i>
              Daily tax report email {enrolled ? '— ON' : '— OFF'}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              {enrolled
                ? 'Every morning (06:00 UTC) we email you what was set aside for taxes the day before. Turn off anytime.'
                : 'The ledger already tracks your taxes nightly (shadow mode). Turn this on to also receive the daily email report.'}
            </div>
          </div>
          <button
            className={`btn btn-sm ${enrolled ? 'btn-outline-danger' : 'btn-success'}`}
            onClick={toggleEnrollment}
            disabled={enrollBusy}
          >
            {enrollBusy ? 'Saving…' : enrolled ? 'Turn off daily email' : 'Turn on daily email'}
          </button>
        </div>
      )}

      <div className="card-body" style={{ padding: '20px 24px' }}>
        {error && <div className="alert alert-danger">{error}</div>}
        {loading ? (
          <div className="text-center py-4"><div className="spinner-border spinner-border-sm" style={{ color: '#f97316' }} /></div>
        ) : !data || (data.entries || []).length === 0 ? (
          <div className="text-muted" style={{ fontSize: 14 }}>
            No entries yet — the ledger starts writing the night the service activates for your account.
          </div>
        ) : (
          <>
            <div className="row mb-3">
              <div className="col-md-4 mb-3">
                <div className="p-3" style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6, color: '#9a3412' }}>Balance held</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: '#1f2937' }}>{money(data.balance)}</div>
                  <small className="text-muted">Your taxes set aside right now</small>
                </div>
              </div>
              <div className="col-md-4 mb-3">
                <div className="p-3" style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6, color: '#6b7280' }}>This month so far</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: '#f97316' }}>{money(data.monthToDate)}</div>
                  <small className="text-muted">To be filed at month end</small>
                </div>
              </div>
            </div>

            <div className="p-3 mb-3" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, fontSize: 13, color: '#1e40af' }}>
              <i className="fas fa-shield-halved mr-2"></i>
              These are your sales taxes, set aside for their monthly filing. FodyFee custodies them and returns the exact filed amount the day before your state's debit. Cash-sale taxes are reported but never held (cash never passes through us).
            </div>

            <div className="table-responsive">
              <table className="table table-sm align-middle" style={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ borderTop: 'none' }}>Day</th>
                    <th style={{ borderTop: 'none' }}>Type</th>
                    <th style={{ borderTop: 'none', textAlign: 'right' }}>Amount</th>
                    <th style={{ borderTop: 'none' }}>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.entries || []).map((e) => (
                    <tr key={e.id}>
                      <td className="text-nowrap">{e.sourceDate || (e.createdAt || '').slice(0, 10)}</td>
                      <td>
                        <span className={`badge ${e.amount < 0 ? 'bg-danger' : 'bg-success'}`}>{TYPE_LABELS[e.type] || e.type}</span>
                        {e.shadow ? ' ' : ''}{e.shadow && <span className="badge bg-secondary">shadow</span>}
                      </td>
                      <td className={`text-end text-nowrap fw-bold ${e.amount < 0 ? 'text-danger' : ''}`}>
                        {e.amount < 0 ? '−' : ''}{money(Math.abs(e.amount || 0))}
                      </td>
                      <td>
                        <small className="text-muted">
                          {e.note || ''}
                          {e.cashTax > 0 ? ` · cash taxes reported (not held): ${money(e.cashTax)}` : ''}
                        </small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TaxHoldingsTab;
